import React from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

export default function ReleaseNotes() {
  const navigate = useNavigate();

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h2 className="text-lg font-bold text-green-700 border-b border-green-200 pb-1 mb-3 mt-6">{title}</h2>
      {children}
    </div>
  );

  const Sub = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="text-base font-bold text-green-800 mb-2">{title}</h3>
      {children}
    </div>
  );

  const Tip = ({ text }) => (
    <div className="bg-green-50 border-l-4 border-green-500 px-4 py-2 my-2 text-sm text-gray-700 italic">💡 {text}</div>
  );

  const Warn = ({ text }) => (
    <div className="bg-yellow-50 border-l-4 border-yellow-500 px-4 py-2 my-2 text-sm text-gray-700 italic">⚠️ {text}</div>
  );

  const Ul = ({ items }) => (
    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ul>
  );

  const Ol = ({ items }) => (
    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ol>
  );

  const P = ({ text }) => <p className="text-sm text-gray-700 mb-2" dangerouslySetInnerHTML={{__html: text}} />;

  const Tbl = ({ headers, rows }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-green-800 text-white">
          <tr>{headers.map((h,i) => <th key={i} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row,i) => (
            <tr key={i} className={i%2===0?"bg-white":"bg-green-50"}>
              {row.map((cell,j) => <td key={j} className="px-3 py-1.5 border-b border-gray-100 text-xs">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Release Notes</span>
          <button onClick={() => navigate("/manual")} className="ml-auto bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-1.5 rounded">← Back to User Manual</button>
        </div>
        <div className="flex-1 overflow-auto px-8 py-6" style={{maxWidth:"900px"}}>

          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <div className="text-5xl mb-2">📜</div>
            <h1 className="text-3xl font-bold text-green-800">GolfSocietyManager — Release Notes</h1>
            <div className="text-green-700 italic text-sm mt-1">Full version history, oldest to newest</div>
          </div>

          <P text="This page keeps a record of what's changed release to release. For how GolfSocietyManager works today, see the <a href='/manual'>User Manual</a>." />

          <Section title="Groups: Drag-and-Drop, One-Click Drop-Out, More Reliable Auto Group">
            <Sub title="Drag-and-Drop Groups">
              <P text="Every player row on the Groups screen can be dragged directly onto another player's row to move them into that group — no need to click into the group number and retype it." />
              <Ol items={[
                "Click and hold the ⠿ handle at the start of a player's row",
                "Drag it onto any player who's already in the group you want to move them to",
                "Drop — the move saves immediately",
              ]} />
              <Warn text="Needs a mouse or trackpad — works on the website and the V5 Desktop App, but not by touch on a phone or tablet. Use the group number field (click to edit) there instead." />
            </Sub>
            <Sub title="Removing a Drop-Out">
              <P text="Real drop-outs on the day are a single click from the Groups screen itself, rather than having to go back to Players." />
              <Ol items={[
                "On the Groups screen, click the small ✕ next to the player's name",
                "Confirm the removal",
                "They disappear from Groups immediately, and their Playing tick on the Players screen is cleared automatically",
              ]} />
              <Tip text="This only unticks Playing — it doesn't delete the player from your society. Add them back at any time by ticking Playing again on the Players screen." />
            </Sub>
            <Sub title="More Reliable Auto Group">
              <P text="The Auto Group rules stayed the same, but the engine behind them was rebuilt for reliability — especially for late roster changes (a player added or dropped after groups were first set), where groups previously could end up the wrong size or with a buggy user left unpaired." />
            </Sub>
          </Section>

          <Section title="Season Points (Order of Merit), Course Aerial Photos, Save Game Update">
            <Sub title="Season Points (Order of Merit) & Season Championship">
              <P text="The Player Stats page gained a running season points total, in the style of a club Order of Merit — each competition's places score points, with the top scorer leading. A Season Championship view alongside it shows each player's best 5 results for the season, for deciding the overall winner." />
            </Sub>
            <Sub title="Course Aerial Photos">
              <P text="Any course imported via the UK Golf Database now automatically fetches a real aerial photo of the course and shows it above the course details, using the club's real location." />
              <Tip text="Courses added before this feature won't have a photo automatically — re-importing the course through the UK Golf Database adds one." />
            </Sub>
            <Sub title="Save Game — Update Existing">
              <P text="Save Game now shows existing saved games with an Update button next to each one, so a recurring fixture can be kept up to date in place instead of piling up near-duplicate saves." />
            </Sub>
            <Sub title="Winners & Losers — Refined Scope">
              <P text="The Winners & Losers podium shows the top finishers, plus anyone who won a prize (nearest pin, nearest pin in 2, longest drive) or scored a 2. Everyone else is entered into the Losers Raffle. Resetting a game also correctly clears any previously saved prize winners." />
            </Sub>
          </Section>

          <Section title="Handicap Automation, Co-Organiser Logins, Mail Merge & Voting">
            <P text="This release added an optional automatic handicap adjustment system, let a second person log in as a full organiser under their own name, brought mail merge with one-click voting into the app, and closed several reliability gaps — including permanently keeping hole-by-hole scores once a round is archived." />
            <Tbl headers={["Feature","Before","After"]} rows={[
              ["Handicaps","Set and edited manually only","Also: optional automatic rules in Changeable Settings — new member caps, a maximum handicap, and automatic post-round adjustment based on points scored"],
              ["Organiser Access","One login per society","Also: a second person can log in as a full co-organiser with their own email and password"],
              ["Member Communication","No built-in mass email","Also: Mail Merge with one-click voting links (Golf & Dinner / Golf Only / Dinner Only / Buggy) embedded right in the email"],
              ["Player Portal Sign-Ups","Unmatched names needed a manual fix","Also: a Link Accounts page to match them yourself"],
              ["Vote Tracking","Only visible via the public voting page","Also: a dedicated Vote Results page with a reliable summary"],
              ["Archived Rounds","Only the final total was kept","Also: every hole's score is kept permanently once archived"],
              ["Score Entry","No export","Also: Export to Excel button"],
              ["Tuesday Golf","Anyone with the sign-up PIN could change the date/time","Also: date/time changes need a separate organiser PIN (set in Changeable Settings)"],
              ["Deleting a Player","Removed all their scores/results if they had any","Now: deactivated instead, keeping every past score and result intact"],
            ]} />
            <Sub title="Automatic Handicap Adjustment (Changeable Settings)">
              <P text="An optional, configurable set of handicap rules for societies that self-manage their members' handicap rather than importing it from an official source — see Section 7.4 of the User Manual for how this works today." />
            </Sub>
            <Sub title="Co-Organiser Logins">
              <P text="A second person can be given full organiser access under their own email and password, instead of sharing a login — see Section 14.1 of the User Manual." />
            </Sub>
            <Sub title="Mail Merge + One-Click Voting">
              <P text="Send a real email to members with merge fields filled in automatically, and let them vote for the next event with a single tap — see Section 14.2 of the User Manual." />
            </Sub>
            <Sub title="Link Accounts">
              <P text="A page to manually match a Player Portal account to the right roster entry when a typed name doesn't exactly match — see Section 14.4 of the User Manual." />
            </Sub>
            <Sub title="Vote Results">
              <P text="A dedicated, always-reliable summary of who has voted for the next event and what they chose — see Section 14.3 of the User Manual." />
            </Sub>
            <Sub title="Hole-by-Hole Scores Kept Forever">
              <P text="Archiving a round's results keeps every hole's score permanently, not just the final total — so a player's full card is always available later, even long after the next event has been set up." />
            </Sub>
            <Sub title="Tuesday Golf: Organiser-Only Date/Time">
              <P text="Anyone with the sign-up PIN can still add or remove themselves from a slot, but changing the round's actual date or tee time needs a separate organiser PIN, set in Changeable Settings." />
            </Sub>
          </Section>

          <Section title="UK Golf Course Database & AI Validate Scores">
            <P text="This release added two ways to keep data accurate with far less manual work: a real UK golf course database for instant, error-free course setup, and an AI check that catches likely score-entry mistakes before a round is finished." />
            <Sub title="UK Golf Course Database">
              <P text="On the Course Setup page, the UK Golf Database button looks up real, published data for thousands of UK golf clubs — no photograph needed, and no risk of a typo or a misread scorecard, since it's pulled from a published course database rather than typed in or read from a photo. Full walkthrough in Section 5.4 of the User Manual." />
            </Sub>
            <Sub title="AI Validate Scores">
              <P text="On the individual Score Entry screen, the Validate Scores button asks Claude to review every score entered so far and flag anything that looks like a likely typing mistake — not a bad golf shot. Full details in Section 5.5 of the User Manual." />
            </Sub>
          </Section>

          <Section title="Desktop App & Offline Mode, Save Game / Load Game">
            <Tbl headers={["Feature","Before","After"]} rows={[
              ["Desktop App","Not available","Installable Windows app — works with no internet connection"],
              ["Offline Score Entry","Not available","Enter and save scores on the course with zero signal"],
              ["Cloud Sync","Not available","One-click Sync Now pulls your event/players/course down and pushes scores back up"],
              ["Save Game / Load Game","Not available","Save a full event setup as a reusable template, reload it in one click next time"],
            ]} />
            <Sub title="Save Game / Load Game">
              <P text="On the Players screen toolbar, two buttons let you save and reuse a complete event setup — useful for a regular fixture where the same players, format and course come up again and again. Full details in Section 5.1 of the User Manual." />
            </Sub>
            <Sub title="V5 Desktop App — Offline Mode">
              <P text="Install the Windows app, activate it once, then use Sync Now before losing signal — the app works fully offline for the rest of the round, syncing back automatically once reconnected. Full details in Section 13 of the User Manual." />
            </Sub>
          </Section>

          <Section title="Multi-Day Competitions, Player Management & Groups Improvements">
            <Tbl headers={["Feature","Before","After"]} rows={[
              ["Multi-Day Competitions","Not available","Archive multiple individual days then combine into overall results table"],
              ["Multi Archive page","Not available","Dedicated page showing combined results with day-by-day breakdown and totals"],
              ["Delete Player","Not available","Select player row to highlight, then click Delete Player button in toolbar"],
              ["Row highlight on Players page","Not available","Click any player row to highlight it in blue — shows which player is selected"],
              ["Edit Handicap on Groups page","Not available","Click any handicap number to edit inline — saves directly to database"],
              ["Society name in sidebar","Not available","Society name and organiser email shown prominently in admin sidebar"],
            ]} />
            <P text="At the time, deleting a player removed them from the player list but did not affect archived results — historical records were preserved. This was later superseded: deleting a player with any recorded scores or results now deactivates them instead of removing them, so nothing about their history needs a confirmation step at all — see Section 5.1 of the User Manual for how deletion works today." />
            <Tip text="Multi-Day Competitions are documented as a current, live workflow in Section 2 of the User Manual, not repeated here." />
          </Section>

          <Section title="Digital Marker Score Verification, Player Portal, Inter-Club Match Play, Score Locking">
            <P text="A major feature release adding digital marker score verification, a player portal, inter-club match play, score locking, and a super admin dashboard." />
            <Tbl headers={["Feature","Before","After"]} rows={[
              ["Score Verification","Not available","Full digital verification — player logs in, confirms partner card, score locks"],
              ["Player Portal","Not available","golfsocietymanager.com/choose-login — player login, results and card verification"],
              ["Player Registration","Not available","Players self-register with name matched to society records"],
              ["Score Locking","Not available","Verified scores locked — cannot be edited by anyone"],
              ["Admin Unlock","Not available","Admin can unlock with mandatory reason — fully audited"],
              ["Marker Assignment","Not available","Auto-assign or manual — one marker per player, saves automatically"],
              ["HCP Review column","Not available","Players opt in on Players page — only shows for eligible formats"],
              ["Inter-Club Match Play","Not available","Team names, running score header, archive and export"],
              ["Better Ball Medal / Stroke Play","Not available","Added as pair formats with correct scoring"],
              ["Greensomes Medal","Not available","Added as pair format"],
              ["Super Admin dashboard","Not available","View all societies, log in as any society, delete societies"],
              ["Unified login page","Not available","golfsocietymanager.com/choose-login — Player or Organiser choice"],
              ["Competition Type dropdown","Present","Removed — pair/team formats auto-detected from game type"],
            ]} />
            <Tip text="Marker assignment shown above is historical — players now declare their own marker at the tee. See Section 12 of the User Manual for how this works today." />
          </Section>

        </div>
      </div>
    </Layout>
  );
}
